import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API, getAuthHeaders } from '../shared';
import { generateWhatsAppLink } from './WhatsAppButton';
import { MessageCircle, X, Phone } from 'lucide-react';

/**
 * Floating WhatsApp Button (FAB)
 * - Fixed bottom-right
 * - Reads agency phone from agency settings (if available)
 * - Opens wa.me link in a NEW TAB (target=_blank + noopener,noreferrer)
 * - NEVER inside an iframe
 * - Logs the click for analytics
 */
const WhatsAppFAB = () => {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [agencyPhone, setAgencyPhone] = useState('');
  const [agencyName, setAgencyName] = useState('وكالات صنهاجة للسفر');

  useEffect(() => {
    // Try to load agency contact from settings; fall back silently
    if (!user) return;
    axios.get(`${API}/agency-settings`, { headers: getAuthHeaders() })
      .then(r => {
        const s = r.data || {};
        if (s.phone) setAgencyPhone(s.phone);
        if (s.agency_name) setAgencyName(s.agency_name);
      })
      .catch(() => { /* not all roles can read; ignore */ });
  }, [user]);

  if (!user) return null; // hide on login screen

  // Pre-compute wa.me URL for the anchor link (NO window.open, NO window.location)
  const phone = agencyPhone || '213555000000';
  const defaultMsg = `السلام عليكم،\n\nأرغب في الاستفسار عن خدمات ${agencyName}.`;
  const waURL = generateWhatsAppLink(phone, defaultMsg);

  const trackFabClick = () => {
    // eslint-disable-next-line no-console
    console.log('[WhatsApp FAB Click]', { timestamp: new Date().toISOString(), phone });
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'whatsapp_fab_click', { phone });
    }
  };

  return (
    <>
      {/* Quick action sheet */}
      {open && (
        <div className="fixed bottom-24 left-6 z-[100] bg-white rounded-2xl shadow-2xl border border-gray-200 w-72 overflow-hidden animate-in slide-in-from-bottom-2" data-testid="whatsapp-fab-sheet">
          <div className="bg-gradient-to-l from-green-600 to-green-700 text-white p-4 flex items-center justify-between">
            <div>
              <p className="font-bold">{agencyName}</p>
              <p className="text-xs opacity-90">عادةً نرد خلال دقائق</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-700">مرحباً 👋 كيف يمكننا مساعدتك؟</p>
            {waURL ? (
              <a
                href={waURL}
                target="_blank"
                rel="noopener noreferrer external"
                referrerPolicy="no-referrer"
                onClick={(e) => {
                  // EXTERNAL LINK SAFEGUARD — wa.me only, bypass router
                  e.stopPropagation();
                  if (waURL.includes('wa.me')) {
                    e.preventDefault();
                    window.open(waURL, '_blank', 'noopener,noreferrer');
                  }
                  trackFabClick();
                  setOpen(false);
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium transition"
                data-testid="whatsapp-fab-send"
              >
                <MessageCircle className="h-4 w-4" /> ابدأ المحادثة
              </a>
            ) : (
              <button disabled className="w-full bg-gray-300 text-white py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium opacity-60 cursor-not-allowed">
                <MessageCircle className="h-4 w-4" /> رقم الوكالة غير محدد
              </button>
            )}
            {agencyPhone && (
              <a
                href={`tel:${agencyPhone}`}
                className="w-full bg-brand-50 hover:bg-brand-100 text-brand-700 border border-brand-200 py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium transition"
              >
                <Phone className="h-4 w-4" /> اتصال هاتفي
              </a>
            )}
            <p className="text-xs text-gray-400 text-center pt-1">
              يفتح في WhatsApp الخاص بك
            </p>
          </div>
        </div>
      )}

      {/* Main floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-6 left-6 z-[100] bg-green-600 hover:bg-green-700 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform"
        title="WhatsApp"
        aria-label="WhatsApp"
        data-testid="whatsapp-fab-btn"
        style={{ boxShadow: '0 8px 24px rgba(34,197,94,0.4)' }}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
        )}
      </button>
    </>
  );
};

export default WhatsAppFAB;
